import { useState } from 'react'

import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { defineChain } from 'viem'
import { WagmiProvider } from 'wagmi'

import { SolanaAdapter } from '@reown/appkit-adapter-solana'
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import '@reown/appkit-ui/jsx'
import '@reown/appkit-wallet-button'
import { createAppKitWalletButton } from '@reown/appkit-wallet-button'
import * as chains from '@reown/appkit/networks'
import type { AppKitNetwork } from '@reown/appkit/networks'
import { createAppKit } from '@reown/appkit/react'

import { AppKitProvider } from '../context/AppKitContext'
import { useRemoveMagicKey } from './hooks/useRemoveMagicKey'
import { router } from './router'

const projectId = import.meta.env.VITE_PROJECT_ID
if (!projectId) {
  throw new Error('VITE_PROJECT_ID environment variable is not set')
}

const arcMainnet = defineChain({
  id: 5042,
  name: 'Arc',
  nativeCurrency: {
    name: 'USDC',
    symbol: 'USDC',
    decimals: 18
  },
  rpcUrls: {
    default: {
      http: ['']
    }
  },
  blockExplorers: {
    default: {
      name: 'Arc Explorer',
      url: ''
    }
  }
})

const networks = [
  ...Object.values(chains).filter(
    chain => (chain as any).chainNamespace !== 'bip122' && (chain as any).id
  ),
  arcMainnet
] as unknown as [AppKitNetwork, ...AppKitNetwork[]]

const wagmiAdapter = new WagmiAdapter({
  projectId,
  networks
})

const solanaAdapter = new SolanaAdapter()

const appKit = createAppKit({
  adapters: [wagmiAdapter, solanaAdapter],
  networks,
  metadata: {
    name: 'Web Wallet App',
    description: 'Web Wallet App',
    url: 'https://web-wallet.walletconnect.org',
    icons: ['https://avatars.githubusercontent.com/u/179229932']
  },
  projectId,
  allWallets: 'HIDE',
  enableWallets: false
})

const appKitWalletButton = createAppKitWalletButton()

export function App() {
  const [queryClient] = useState(() => new QueryClient())
  useRemoveMagicKey()

  const ProfiledRouterProvider = Sentry.withProfiler(RouterProvider)

  return (
    <WagmiProvider config={wagmiAdapter.wagmiConfig} reconnectOnMount={false}>
      <QueryClientProvider client={queryClient}>
        <AppKitProvider
          appkit={appKit}
          wagmiConfig={wagmiAdapter.wagmiConfig}
          appKitWalletButton={appKitWalletButton}
        >
          <ProfiledRouterProvider router={router} />
        </AppKitProvider>
      </QueryClientProvider>
    </WagmiProvider>
  )
}
